import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import isArray from 'lodash/isArray';
import { FormCheckbox, SideModal } from '../../../components';
import { parseJSON, publicCase } from '../../../utils/string';
import { exportOptionsMap, exportOptionsKeyMap } from '../../../constants/dashboard.constants';
import './ExportButtonOptions.scss';

const ExportButtonOptions = ({ onSubmit, onCancel: onCancelCB }) => {
  const cookingMethod = useSelector(state => state.chickenDashboard.cookingMethod);
  const exportOptions = exportOptionsMap[cookingMethod];
  const exportOptionsKey = exportOptionsKeyMap[cookingMethod];
  
  const [checkedOptions, setCheckedOptions] = useState(() => {
    const initialStorageOpsJSON = localStorage.getItem(exportOptionsKey);
    const initialStorageOps = initialStorageOpsJSON ? parseJSON(initialStorageOpsJSON) : [];
    const initialOps = [];

    if (isArray(initialStorageOps)) {
      for (const option of initialStorageOps) {
        if (exportOptions.includes(option)) {
          initialOps.push(option);
        }
      }
    }

    return initialOps;
  });

  const saveExportOptions = () => {
    localStorage.setItem(exportOptionsKey, JSON.stringify(checkedOptions));
  };

  const onConfirm = () => {
    saveExportOptions();
    onSubmit(checkedOptions);
  };

  const onCancel = () => {
    saveExportOptions();
    onCancelCB();
  };

  const getOptionLabel = option => {
    switch (option) {
      case 'CONTAINERS_REGISTERED_PRODUCTION': {
        return 'Registered Production Containers';
      }
      case 'CONTAINERS_REGISTERED_SCHEDULED': {
        return 'Registered Scheduled Production Containers';
      }
      case 'CONTAINERS_RECOMMENDED_PRODUCTION': {
        return 'Recommended Production Containers';
      }
      case 'CONTAINERS_RECOMMENDED_SCHEDULED': {
        return 'Recommended Scheduled Production Containers';
      }
      case 'TRAYS_REGISTERED': {
        return 'Registered Trays';
      }
      case 'TRAYS_RECOMMENDED': {
        return 'Recommended Trays';
      }
      case 'TOTAL_HOLD_TIME_REPORT': {
        return 'Total Hold Time';
      }
      case 'TIME_IN_CHUTES_ONLY_HOLD_TIME_REPORT': {
        return 'Total Hold Time in Chutes Only';
      }
      case 'PRS': {
        if (cookingMethod === 'FRIED') {
          return 'Pan Return Station (PRS)';
        } else if (cookingMethod === 'GRILLED') {
          return 'Merco Tray Return Station (MTRS)';
        } else {
          return option;
        }
      }
      case 'STOCK_OUT': {
        return 'Material Stock Outs';
      }
      case 'TASK_DELAY': {
        return 'Breading Task Delay';
      }
      case 'FRYER_CYCLES': {
        if (cookingMethod === 'FRIED') {
          return 'Fryer Cycles';
        } else if (cookingMethod === 'GRILLED') {
          return 'Grill Cycles';
        } else {
          return option;
        }
      }
      case 'FRYER_AVAILABILITY': {
        if (cookingMethod === 'FRIED') {
          return 'Fryers Availability';
        } else if (cookingMethod === 'GRILLED') {
          return 'Grills Availability';
        } else {
          return option;
        }
      }
      default: {
        return publicCase(option);
      }
    }
  };

  const onChangeOption = option => {
    const optionIndex = checkedOptions.findIndex(item => item === option);
    const newCheckedOptions = [...checkedOptions];

    if (optionIndex === -1) {
      newCheckedOptions.push(option);
    } else {
      newCheckedOptions.splice(optionIndex, 1);
    }

    setCheckedOptions(newCheckedOptions);
  }

  const onClickSelectAll = () => {
    if (checkedOptions.length === exportOptions.length) {
      setCheckedOptions([]);
    } else {
      setCheckedOptions([...exportOptions]);
    }
  };

  const body = (
    <div className="export-btn-ops">
      <p>Select the categories you need to export</p>

      <div className="export-btn-ops-controls">
        <FormCheckbox
          label="All Categories"
          checked={checkedOptions.length === exportOptions.length}
          onChange={onClickSelectAll}
          className="export-btn-ops-select-all"
          rounded
        />

        {exportOptions.map(option => {
          const checked = checkedOptions.includes(option);

          return (
            <FormCheckbox
              key={option}
              label={getOptionLabel(option)}
              checked={checked}
              onChange={() => onChangeOption(option)}
              rounded
            />
          );
        })}
      </div>
    </div>
  );

  return (
    <SideModal
      title="Export Data"
      onCancel={onCancel}
      onConfirm={onConfirm}
      modalBody={body}
      className="export-options-modal"
      size="sm"
      confirmText="Save & Export"
      confirmDisabled={!checkedOptions.length}
    />
  );
};

export default ExportButtonOptions;